import React, { useMemo, useState } from "react";
import {
  FiArrowDownRight,
  FiArrowUpRight,
  FiDownload,
  FiSearch,
} from "react-icons/fi";
import { DASHBOARD_ASSETS } from "../../constants/dashboard.ts";
import { DashboardSidebar, DashboardTopbar } from "./page.tsx";

type HistoryRow = {
  id: string;
  date: string;
  pair: string;
  direction: "CALL" | "PUT";
  entry: string;
  confidence: number;
  result: "Ganho" | "Perda";
};

const HISTORY_ROWS: HistoryRow[] = [
  { id: "h1", date: "28/06/26 11:17", pair: "EURUSD", direction: "CALL", entry: "11:17", confidence: 80, result: "Ganho" },
  { id: "h2", date: "28/06/26 08:01", pair: "GBPUSD", direction: "PUT", entry: "08:01", confidence: 94, result: "Ganho" },
  { id: "h3", date: "28/06/26 02:56", pair: "USDCAD", direction: "CALL", entry: "02:56", confidence: 57, result: "Perda" },
  { id: "h4", date: "28/06/26 00:01", pair: "USDJPY", direction: "PUT", entry: "00:01", confidence: 88, result: "Ganho" },
  { id: "h5", date: "27/06/26 19:35", pair: "CADCHF", direction: "PUT", entry: "19:35", confidence: 90, result: "Ganho" },
  { id: "h6", date: "27/06/26 15:11", pair: "EURJPY", direction: "CALL", entry: "15:11", confidence: 72, result: "Perda" },
  { id: "h7", date: "27/06/26 12:15", pair: "EURUSD", direction: "CALL", entry: "12:15", confidence: 55, result: "Ganho" },
  { id: "h8", date: "27/06/26 06:53", pair: "USDCAD", direction: "CALL", entry: "06:53", confidence: 71, result: "Perda" },
  { id: "h9", date: "27/06/26 04:22", pair: "GBPUSD", direction: "PUT", entry: "04:22", confidence: 90, result: "Perda" },
  { id: "h10", date: "26/06/26 22:52", pair: "USDJPY", direction: "PUT", entry: "22:52", confidence: 74, result: "Ganho" },
  { id: "h11", date: "26/06/26 18:14", pair: "CADCHF", direction: "CALL", entry: "18:14", confidence: 82, result: "Ganho" },
  { id: "h12", date: "26/06/26 13:44", pair: "EURJPY", direction: "PUT", entry: "13:44", confidence: 66, result: "Perda" },
];

type DashboardHistoryProps = {
  preview?: boolean;
};

export default function DashboardHistory({ preview = false }: DashboardHistoryProps) {
  const [query, setQuery] = useState("");
  const [asset, setAsset] = useState("ALL");
  const [result, setResult] = useState("ALL");

  const filteredRows = useMemo(() => {
    return HISTORY_ROWS.filter((row) => {
      const matchesQuery = row.pair.toLowerCase().includes(query.trim().toLowerCase());
      const matchesAsset = asset === "ALL" || row.pair === asset;
      const matchesResult = result === "ALL" || row.result === result;

      return matchesQuery && matchesAsset && matchesResult;
    });
  }, [asset, query, result]);

  return (
    <div className="min-h-screen bg-[#f4f7f1] text-[#0d2a1f]">
      <DashboardSidebar active="Historico" preview={preview} />

      <main className="min-h-screen xl:pl-[270px]">
        <DashboardTopbar />

        <div className="mx-auto max-w-6xl px-5 py-7 xl:px-6">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-[-0.04em] text-[#0d2a1f]">
                Histórico
              </h1>
              <p className="mt-1.5 text-sm font-medium text-[#73827b]">
                Todos os sinais passados. Dados de demonstracao ficticios.
              </p>
            </div>

            <button
              type="button"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-[#dfe8e1] bg-white px-4 text-sm font-black text-[#0d2a1f] shadow-[0_10px_24px_rgba(20,44,33,0.06)] transition hover:border-[#bfe8d3] hover:bg-[#f8fbf6]"
            >
              <FiDownload className="h-4 w-4" />
              Exportar CSV
            </button>
          </div>

          <section className="rounded-[20px] border border-[#dfe8e1] bg-white p-3 shadow-[0_12px_30px_rgba(20,44,33,0.07)]">
            <div className="grid gap-2.5 md:grid-cols-[1.05fr_1fr_1fr]">
              <label className="flex h-10 items-center gap-2.5 rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-3 text-[#73827b]">
                <FiSearch className="h-4 w-4" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Pesquisar ativo..."
                  className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#153329] outline-none placeholder:text-[#8a9a93]"
                />
              </label>

              <select
                value={asset}
                onChange={(event) => setAsset(event.target.value)}
                className="h-10 rounded-xl border border-[#20c77a] bg-[#f4f7f1] px-3 text-sm font-semibold text-[#153329] outline-none ring-4 ring-[#20c77a]/10"
              >
                <option value="ALL">Todos os ativos</option>
                {DASHBOARD_ASSETS.map((pair) => (
                  <option key={pair} value={pair}>
                    {pair}
                  </option>
                ))}
              </select>

              <select
                value={result}
                onChange={(event) => setResult(event.target.value)}
                className="h-10 rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-3 text-sm font-semibold text-[#153329] outline-none transition focus:border-[#20c77a] focus:ring-4 focus:ring-[#20c77a]/10"
              >
                <option value="ALL">Todos os resultados</option>
                <option value="Ganho">Ganho</option>
                <option value="Perda">Perda</option>
              </select>
            </div>
          </section>

          <section className="mt-6 overflow-hidden rounded-[20px] border border-[#dfe8e1] bg-white shadow-[0_12px_30px_rgba(20,44,33,0.07)]">
            <div className="overflow-x-auto">
              <table className="min-w-[820px] w-full border-collapse text-left">
                <thead>
                  <tr className="bg-[#f1f4ed] text-[11px] font-black uppercase tracking-wide text-[#6f8178]">
                    <th className="px-4 py-3">Data / Hora</th>
                    <th className="px-4 py-3">Ativo</th>
                    <th className="px-4 py-3">Direcao</th>
                    <th className="px-4 py-3">Entrada</th>
                    <th className="px-4 py-3">Expiracao</th>
                    <th className="px-4 py-3">Conf. IA</th>
                    <th className="px-4 py-3">Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const isCall = row.direction === "CALL";
                    const DirectionIcon = isCall ? FiArrowUpRight : FiArrowDownRight;

                    return (
                      <tr
                        key={row.id}
                        className="border-t border-[#214b3d]/35 text-[13px] font-semibold text-[#71837b]"
                      >
                        <td className="px-4 py-3 tabular-nums">{row.date}</td>
                        <td className="px-4 py-3 text-sm font-black text-[#0d2a1f]">{row.pair}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 text-sm font-black ${
                              isCall ? "text-[#078a50]" : "text-[#e11d48]"
                            }`}
                          >
                            <DirectionIcon className="h-4 w-4" />
                            {row.direction}
                          </span>
                        </td>
                        <td className="px-4 py-3 tabular-nums">{row.entry}</td>
                        <td className="px-4 py-3">M5 · 5 min</td>
                        <td className="px-4 py-3 tabular-nums">{row.confidence}%</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-black ${
                              row.result === "Ganho"
                                ? "bg-[#dff6ea] text-[#087a45]"
                                : "bg-[#ffe5ec] text-[#e11d48]"
                            }`}
                          >
                            {row.result}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
